"use client";

import React from "react";
import { CircleCheck as CheckCircle2 } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const flooringTypes = [{
  id: 1,
  title: "EV Charging",
  description: "Future-proof your property with high-speed electric vehicle charging stations. We handle everything from load management to final testing.",
  image: {
    src: "https://adelectricals.ie/wp-content/uploads/2026/06/8.png?v=q5kfh85wgxq",
    srcset: "https://adelectricals.ie/wp-content/uploads/2026/06/8.png?v=swz63hvf2dc-wvc-srcset",
    sizes: "https://adelectricals.ie/wp-content/uploads/2026/06/8.png?v=omfiwbgn0cg-wvc-sizes",
    alt: "Professional EV charger installation"
  },
  durability: "IP65 Rated",
  woodType: "Smart Integration"
}, {
  id: 2,
  title: "Smart Homes",
  description: "Intelligent automation for lighting, heating, and security. Seamlessly control your entire environment from a single interface.",
  image: {
    src: "https://adelectricals.ie/wp-content/uploads/2026/06/11-1.png?v=vsg13h85lc",
    srcset: "https://adelectricals.ie/wp-content/uploads/2026/06/11-1.png?v=gzunuvewx1r-wvc-srcset",
    sizes: "https://adelectricals.ie/wp-content/uploads/2026/06/11-1.png?v=0pud3p9oclaa-wvc-sizes",
    alt: "Smart home control panel"
  },
  durability: "24/7 Active",
  woodType: "KNX / Loxone"
}, {
  id: 3,
  title: "Fuse Upgrades",
  description: "Modern consumer units with advanced surge protection. Essential for safety and meeting current electrical regulations.",
  image: {
    src: "https://adelectricals.ie/wp-content/uploads/2026/06/4-2.png?v=hpd46wmqqb",
    srcset: "https://adelectricals.ie/wp-content/uploads/2026/06/4-2.png?v=h37hb9se0jg-wvc-srcset",
    sizes: "https://adelectricals.ie/wp-content/uploads/2026/06/4-2.png?v=i4xvx1iypsg-wvc-sizes",
    alt: "Modern electrical consumer unit"
  },
  durability: "High Capacity",
  woodType: "18th Edition"
}, {
  id: 4,
  title: "Solar Systems",
  description: "Harness renewable energy with precision-engineered solar PV installations and battery storage solutions for total independence.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/solar_pv_installation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/solar_pv_installation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/solar_pv_installation.png-wvc-sizes",
    alt: "Solar panel array on roof"
  },
  durability: "25 Year Life",
  woodType: "Hybrid Inverters"
}];
const features = [{
  id: 1,
  text: "Fully Certified & Insured Professionals"
}, {
  id: 2,
  text: "Transparent Pricing with No Hidden Costs"
}, {
  id: 3,
  text: "24/7 Emergency Support for Critical Issues"
}, {
  id: 4,
  text: "Cutting-Edge Smart Home & EV Technology"
}, {
  id: 5,
  text: "Uncompromising Commitment to Safety Standards"
}, {
  id: 6,
  text: "Tailored Solutions for Every Project Scale"
}];
export default function DasParkett() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262072" id="specialized-solutions" className="relative py-24 md:py-32 dark bg-background text-foreground overflow-hidden">
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/circuit_grid_pattern.png)] bg-repeat opacity-[0.05] pointer-events-none mix-blend-screen" aria-hidden="true" />

      <div className="container mx-auto px-4 md:px-6 relative z-10" ref={ref}>
        <div className={`mb-16 md:mb-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-default font-bold tracking-tighter text-foreground mb-6">
            Expertise You Can Trust
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl font-default leading-relaxed">
            At AD Electrical, we combine years of industry experience with a passion for innovation. Our team of certified electricians is dedicated to providing top-tier electrical services that prioritize safety, efficiency, and modern technology. Whether it's a simple home repair or a complex commercial installation, we bring the same level of precision and professionalism to every job.
          </p>
        </div>

        <div className={`mb-20 transition-all duration-1000 delay-100 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h3 className="text-3xl md:text-4xl font-bold mb-8 text-foreground">Why Choose AD Electrical?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => <div key={feature.id} data-index={i} className="flex items-center gap-3 p-4 bg-card/30 border border-border/40 rounded-md">
                <CheckCircle2 className="size-6 text-accent shrink-0" />
                <span className="text-lg font-medium">{feature.text}</span>
              </div>)}
          </div>
        </div>

        <div className={`transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {flooringTypes.map((floor, index) => <CarouselItem key={floor.id} data-index={index} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                  <div className="relative h-80 md:h-96 overflow-hidden bg-muted rounded-lg border border-border/40 shadow-lg hover:shadow-ring/20 hover:border-ring/50 transition-all duration-500 group">
                    <img src={floor.image.src} data-wvc-srcset={floor.image.srcset} data-wvc-sizes={floor.image.sizes} alt={floor.image.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            <div className="flex justify-end gap-4 mt-12 pr-4 md:pr-6">
              <CarouselPrevious className="relative inset-auto translate-y-0 size-14 rounded-md border-border bg-card hover:bg-accent hover:text-accent-foreground shadow-md transition-colors" />
              <CarouselNext className="relative inset-auto translate-y-0 size-14 rounded-md border-border bg-card hover:bg-accent hover:text-accent-foreground shadow-md transition-colors" />
            </div>
          </Carousel>
        </div>

        <div className={`mt-24 text-center transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h3 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">Ready to Discuss Your Project?</h3>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            From minor repairs to major installations, AD Electrical is here to help. Contact us today for a free consultation and experience the difference that professional expertise makes.
          </p>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold px-8 py-6 text-lg rounded-md transition-all duration-300">
            <Link to="/ContactPage">Contact Us Today</Link>
          </Button>
        </div>
      </div>
    </section>;
}